import { useEffect, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import {
  ToggleGroup,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';

import type { Appointment } from '@/react-query/useAppointments';

import type {
  TProcedureStatus,
  TVisitSummary,
} from '../../../../react-query/useVisitSummaries';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import {
  isActionableViewAtom,
  referralsFilterAtom,
  selectedCountsFilterAtom,
} from '../../atoms';
import {
  useRecommendedProceduresGroupedByMouthArea,
  useTreatmentsGroupedByStatus,
} from '../patientStripOdontogram/hooks';
import { isRecommendationFilter } from '../patientStripOdontogram/utils';

import { TreatmentCountFilter, TreatmentCountItem } from './TreatmentCountItem';

export const allStatuses: TProcedureStatus[] = [
  'recommended',
  'unscheduled',
  'scheduled_today',
  'scheduled_future',
  'completed',
];

export const actionableStatuses: TProcedureStatus[] = [
  'recommended',
  'unscheduled',
];

const getStatusesFromFilters = (
  filters: TreatmentCountFilter[],
): TProcedureStatus[] => {
  const newStatuses: TProcedureStatus[] = [];
  filters.forEach((filter) => {
    switch (filter) {
      case TreatmentCountFilter.OPPORTUNITIES:
        newStatuses.push('recommended');
        break;
      case TreatmentCountFilter.UNSCHEDULED:
        newStatuses.push('unscheduled');
        break;
      case TreatmentCountFilter.SCHEDULED:
        newStatuses.push('scheduled_today', 'scheduled_future');
        break;
      case TreatmentCountFilter.COMPLETED:
        newStatuses.push('completed');
        break;
      default:
        break;
    }
  });
  return newStatuses;
};

export const TreatmentCounts = ({
  appointment,
  visitSummary,
  setSelectedStatuses,
  isLoading,
}: {
  appointment?: Appointment;
  visitSummary?: TVisitSummary;
  setSelectedStatuses: (statuses: TProcedureStatus[]) => void;
  isLoading?: boolean;
}) => {
  const [selectedFilters, setSelectedFilters] = useState<
    TreatmentCountFilter[]
  >([]);
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);
  const referralsFilter = useAtomValue(referralsFilterAtom);
  const isRecommendationFilterSelected =
    isRecommendationFilter(selectedCountsFilter);
  const isActionableView = useAtomValue(isActionableViewAtom);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();

  const treatmentsGroupedByStatus = useTreatmentsGroupedByStatus(visitSummary);
  const groupedRecommendations = useRecommendedProceduresGroupedByMouthArea(
    visitSummary,
    true,
  );

  const opportunitiesCount = Object.entries(groupedRecommendations).length;
  const unscheduledTreatmentsCount = Object.entries(
    treatmentsGroupedByStatus.unscheduled,
  ).length;
  const scheduledTodayTreatmentsCount = Object.entries(
    treatmentsGroupedByStatus.scheduled_today,
  ).length;
  const scheduledFutureTreatmentsCount = Object.entries(
    treatmentsGroupedByStatus.scheduled_future,
  ).length;
  const scheduledTreatmentsCount =
    scheduledTodayTreatmentsCount + scheduledFutureTreatmentsCount;
  const completedTreatmentsCount = Object.entries(
    treatmentsGroupedByStatus.completed,
  ).length;

  const handleValueChange = (newSelectedFilters: TreatmentCountFilter[]) => {
    setSelectedFilters(newSelectedFilters);
    const newStatuses = getStatusesFromFilters(newSelectedFilters);
    const defaultStatuses = isActionableView ? actionableStatuses : allStatuses;
    setSelectedStatuses(newStatuses.length ? newStatuses : defaultStatuses);

    trackEvent(mixpanelEvents.huddleSelectTreatmentFilter, {
      status: newStatuses,
      estimatedVisitId: visitSummary?.estimatedVisitId,
      unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
      opportunitiesCount,
      unscheduledTreatmentsCount,
      scheduledTreatmentsCount,
      completedTreatmentsCount,
      pmsPatientId: appointment?.patient.pmsPatientId,
      pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
      imagingPatientId: appointment?.patient.imagingPatientId,
    });
  };

  // Handle recommendation filter changes:
  // If the user selects a recommendation filter in the sidebar, we should
  // deselect all of the treatment count filters in the patient strip and set
  // the statuses to the default based on the view.
  useEffect(() => {
    if (isRecommendationFilter(selectedCountsFilter)) {
      setSelectedFilters([]);
      setSelectedStatuses(isActionableView ? actionableStatuses : allStatuses);
    }
  }, [isActionableView, selectedCountsFilter, setSelectedStatuses]);

  // Handle actionable view changes
  useEffect(() => {
    if (isActionableView) {
      const newSelectedFilters = selectedFilters.filter((filter) =>
        [
          TreatmentCountFilter.OPPORTUNITIES,
          TreatmentCountFilter.UNSCHEDULED,
        ].includes(filter),
      );
      const newStatuses = getStatusesFromFilters(newSelectedFilters);

      setSelectedFilters(newSelectedFilters);
      setSelectedStatuses(
        newStatuses.length ? newStatuses : actionableStatuses,
      );
    } else {
      const newStatuses = getStatusesFromFilters(selectedFilters);
      setSelectedStatuses(newStatuses.length ? newStatuses : allStatuses);
    }

    // Disabling exhaustive-deps because we're setting `selectedFilters` in the
    // useEffect and we want to avoid infinite renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionableView, setSelectedStatuses]);

  // Hide the component if referrals filter is active
  if (referralsFilter !== null) {
    return null;
  }

  return (
    <ToggleGroup
      type="multiple"
      value={selectedFilters}
      onValueChange={handleValueChange}
      className="vai-flex vai-flex-col vai-gap-1"
    >
      <p className="vai-uppercase vai-self-start vai-my-0 vai-font-medium vai-text-gray-500 vai-text-[10px] vai-leading-[13px]">
        <Trans>Treatments</Trans>
      </p>
      <TreatmentCountItem
        disabled={isRecommendationFilterSelected}
        value={TreatmentCountFilter.OPPORTUNITIES}
        count={opportunitiesCount}
        label={t`AI Opportunities`}
        isLoading={isLoading}
      />

      <TreatmentCountItem
        disabled={isRecommendationFilterSelected}
        value={TreatmentCountFilter.UNSCHEDULED}
        count={unscheduledTreatmentsCount}
        label={t`Unscheduled`}
        isLoading={isLoading}
      />

      <TreatmentCountItem
        disabled={isRecommendationFilterSelected}
        value={TreatmentCountFilter.SCHEDULED}
        count={scheduledTreatmentsCount}
        label={t`Scheduled`}
        hide={isActionableView}
        isLoading={isLoading}
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="vai-bg-transparent vai-border-none vai-w-full vai-justify-start vai-p-0 vai-cursor-pointer vai-h-auto"
            asChild
          >
            <TreatmentCountItem
              disabled={isRecommendationFilterSelected}
              value={TreatmentCountFilter.COMPLETED}
              count={completedTreatmentsCount}
              label={t`Completed`}
              hide={isActionableView}
              isLoading={isLoading}
            />
          </TooltipTrigger>
          <TooltipContent>
            <Trans>
              Only treatments completed since the latest images are shown
            </Trans>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </ToggleGroup>
  );
};
